import type React from 'react';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Check, Loader2, Clock } from 'lucide-react';
import { createBooking } from '@/client/sdk.gen';
import { EventTypeResponse, BookingResponse } from '@/client/types.gen';

// Extend the BookingResponse type to include uid
interface ExtendedBookingResponse extends BookingResponse {
  uid: string;
}

interface EventDetailsProps {
  date: Date;
  startTime: string;
  endTime: string;
  eventTypeDetails: EventTypeResponse;
  onBookingCreated: (booking: ExtendedBookingResponse) => void;
  timezone?: string;
}

export function EventDetails({
  date,
  startTime,
  endTime,
  eventTypeDetails,
  onBookingCreated,
  timezone,
}: EventDetailsProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await createBooking({
        path: { event_type_id: eventTypeDetails.id },
        body: {
          start_time: startTime,
          end_time: endTime,
          description: notes,
          invitee: {
            name,
            email,
            timezone:
              timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      });

      if (response.error) {
        throw new Error(`Failed to create booking: ${response.error}`);
      }

      if (response.data) {
        // Add a temporary uid if one doesn't exist in the API response
        const bookingWithUid = {
          ...response.data,
          uid: response.data.id, // For now, just use the id as the uid
        } as ExtendedBookingResponse;

        onBookingCreated(bookingWithUid);
      }
    } catch (err) {
      console.error('Error creating booking:', err);
      setError('Failed to schedule the meeting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format the display time
  const startDateTime = new Date(startTime);
  const formattedTime = format(startDateTime, 'h:mm a');

  return (
    <Card className="overflow-hidden border shadow-md">
      <div className="p-4 border-b bg-muted">
        <h2 className="text-lg font-semibold text-foreground">
          Complete your booking
        </h2>
      </div>

      <CardContent className="p-6">
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-muted p-4 rounded-md mb-4 border">
            <p className="text-sm font-medium text-foreground">
              {eventTypeDetails.name}
            </p>
            <div className="flex items-center mt-2">
              <Clock className="h-4 w-4 text-muted-foreground mr-2" />
              <p className="text-sm text-muted-foreground">
                {format(date, 'EEEE, MMMM d')} at {formattedTime} •{' '}
                {eventTypeDetails.duration} minutes •{' '}
                {timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Name
                </Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium">
                Additional notes
              </Label>
              <Textarea
                id="notes"
                placeholder="Any additional information you'd like to share"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="resize-none min-h-[100px]"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 mt-4 font-medium"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Confirming...
              </>
            ) : (
              'Confirm Booking'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
